/**
 * Israeli Pension Law Engine
 *
 * Encodes Israeli pension rules: product classification, mekadem (annuity
 * coefficient) lookup, tax calculations for different withdrawal scenarios,
 * and contribution reduction modeling.
 *
 * @module domain/retirement/pensionLaw
 */

import type { PensionAsset } from "~/types/assets.types";

// ============================================================================
// PRODUCT CLASSIFICATION
// ============================================================================

export enum PensionProductClass {
  KEREN_PENSIA_KITZBATIT = "KEREN_PENSIA_KITZBATIT",
  KEREN_PENSIA_HONIT = "KEREN_PENSIA_HONIT",
  BITUACH_MENAHALIM = "BITUACH_MENAHALIM",
  KUPAT_GEMEL_KITZBATIT = "KUPAT_GEMEL_KITZBATIT",
  KUPAT_GEMEL_HONIT = "KUPAT_GEMEL_HONIT",
  KUPAT_GEMEL_LEHASHKAA = "KUPAT_GEMEL_LEHASHKAA",
  KEREN_HISHTALMUT = "KEREN_HISHTALMUT",
  CHISACHON_LEKAL_YELED = "CHISACHON_LEKAL_YELED",
  UNKNOWN = "UNKNOWN",
}

export enum PensionWithdrawalType {
  KITZBA = "kitzba",
  HON = "hon",
  MIXED = "mixed",
}

// Hebrew product type strings from masleka
const PRODUCT_TYPE_MAP: Record<string, string> = {
  "פוליסת ביטוח חיים משולב חיסכון": "bituach_menahalim",
  "ביטוח מנהלים": "bituach_menahalim",
  "קרן פנסיה": "keren_pensia",
  "פנסיה": "keren_pensia",
  "קופת גמל": "kupat_gemel",
  "גמל": "kupat_gemel",
  "קופת גמל להשקעה": "kupat_gemel_lehashkaa",
  "גמל להשקעה": "kupat_gemel_lehashkaa",
  "קרן השתלמות": "keren_hishtalmut",
  "קרנות השתלמות": "keren_hishtalmut",
  "השתלמות": "keren_hishtalmut",
  "חסכון לכל ילד": "chisachon_yeled",
  "פוליסת חסכון טהור": "bituach_menahalim",
};

/**
 * Classify a pension product into its legal category based on product type,
 * sug kupa, and sug keren pensia fields from masleka.
 */
export function classifyPensionProduct(product: PensionAsset): PensionProductClass {
  const productTypeValue = product.productType?.value ?? "";
  const sugKupa = product.sugKupa;

  const mapped = PRODUCT_TYPE_MAP[productTypeValue];

  if (mapped === "keren_hishtalmut" || sugKupa === "3") {
    return PensionProductClass.KEREN_HISHTALMUT;
  }

  if (mapped === "chisachon_yeled") {
    return PensionProductClass.CHISACHON_LEKAL_YELED;
  }

  if (mapped === "kupat_gemel_lehashkaa") {
    return PensionProductClass.KUPAT_GEMEL_LEHASHKAA;
  }

  if (mapped === "keren_pensia") {
    if (sugKupa === "2") return PensionProductClass.KEREN_PENSIA_HONIT;
    return PensionProductClass.KEREN_PENSIA_KITZBATIT;
  }

  if (mapped === "bituach_menahalim") {
    return PensionProductClass.BITUACH_MENAHALIM;
  }

  if (mapped === "kupat_gemel") {
    if (sugKupa === "2") return PensionProductClass.KUPAT_GEMEL_HONIT;
    return PensionProductClass.KUPAT_GEMEL_KITZBATIT;
  }

  // Fallback: try to infer from sugKupa alone
  if (sugKupa === "1") return PensionProductClass.KUPAT_GEMEL_KITZBATIT;
  if (sugKupa === "2") return PensionProductClass.KUPAT_GEMEL_HONIT;

  return PensionProductClass.UNKNOWN;
}

/**
 * Determine the default withdrawal type for a product class.
 * For Bituach Menahalim, the sug_kupa field overrides the default:
 *   sug_kupa=1 → kitzbati (full annuity)
 *   sug_kupa=2 → honi (full lump sum)
 *   unknown   → mixed (70% kitzba / 30% hon)
 */
export function getDefaultWithdrawalType(
  productClass: PensionProductClass,
  sugKupa?: string,
): PensionWithdrawalType {
  switch (productClass) {
    case PensionProductClass.KEREN_PENSIA_KITZBATIT:
    case PensionProductClass.KUPAT_GEMEL_KITZBATIT:
      return PensionWithdrawalType.KITZBA;

    case PensionProductClass.KEREN_PENSIA_HONIT:
    case PensionProductClass.KUPAT_GEMEL_HONIT:
    case PensionProductClass.KUPAT_GEMEL_LEHASHKAA:
    case PensionProductClass.CHISACHON_LEKAL_YELED:
      return PensionWithdrawalType.HON;

    case PensionProductClass.BITUACH_MENAHALIM:
      if (sugKupa === "1") return PensionWithdrawalType.KITZBA;
      if (sugKupa === "2") return PensionWithdrawalType.HON;
      return PensionWithdrawalType.MIXED;

    case PensionProductClass.KEREN_HISHTALMUT:
      return PensionWithdrawalType.HON;

    default:
      return PensionWithdrawalType.KITZBA;
  }
}

/**
 * Get the earliest age at which the product becomes accessible.
 */
export function getEligibilityAge(
  productClass: PensionProductClass,
  gender: "male" | "female" = "male",
): number {
  switch (productClass) {
    case PensionProductClass.KUPAT_GEMEL_HONIT:
    case PensionProductClass.KUPAT_GEMEL_LEHASHKAA:
      return 60;

    case PensionProductClass.KEREN_HISHTALMUT:
      return 0; // liquid after 6 years of vesting, not age-dependent

    case PensionProductClass.CHISACHON_LEKAL_YELED:
      return 18;

    default:
      return gender === "female" ? 65 : 67;
  }
}

// ============================================================================
// MEKADEM (ANNUITY COEFFICIENT)
// ============================================================================

/**
 * Default mekadem lookup table.
 * Values represent the number of monthly payments the accumulated pension
 * is divided by to produce a monthly kitzba.
 *
 * Based on Israeli actuarial norms; adjusted by retirement age, gender,
 * and marital status. Married coefficients are higher because the pension
 * must cover a surviving spouse (pension sheerim).
 */
const DEFAULT_MEKADEM_TABLE: Record<string, Record<string, number>> = {
  male_single:   { "60": 240, "62": 230, "64": 222, "65": 218, "67": 212, "70": 200 },
  male_married:  { "60": 265, "62": 256, "64": 248, "65": 244, "67": 238, "70": 225 },
  female_single: { "60": 260, "62": 252, "64": 244, "65": 240, "67": 232, "70": 220 },
  female_married:{ "60": 285, "62": 276, "64": 268, "65": 264, "67": 258, "70": 245 },
};

function interpolateMekadem(table: Record<string, number>, age: number): number {
  const ages = Object.keys(table).map(Number).sort((a, b) => a - b);

  if (age <= ages[0]) return table[String(ages[0])];
  if (age >= ages[ages.length - 1]) return table[String(ages[ages.length - 1])];

  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) {
      const fraction = (age - ages[i]) / (ages[i + 1] - ages[i]);
      return table[String(ages[i])] + fraction * (table[String(ages[i + 1])] - table[String(ages[i])]);
    }
  }
  return table[String(ages[0])];
}

/**
 * Look up the mekadem for a given product. Priority:
 * 1. Product's explicit mekadem from masleka (Bituach Menahalim products have this)
 * 2. Implied mekadem derived from schumKitzbatZikna / projectedSavingsWithPremiums
 *    (Keren Pensia products typically don't have explicit mekadem but do have these)
 * 3. Default actuarial table based on age, gender, marital status
 */
export function getMekadem(
  product: PensionAsset,
  retirementAge: number,
  gender: "male" | "female" = "male",
  isMarried: boolean = false,
): number {
  if (product.mekadem && product.mekadem > 0) {
    return product.mekadem;
  }

  // Derive implied mekadem from masleka's projected pension and savings data.
  // schumKitzbatZikna = projectedSavingsWithPremiums / mekadem
  if (
    product.schumKitzbatZikna &&
    product.schumKitzbatZikna > 0 &&
    product.projectedSavingsWithPremiums &&
    product.projectedSavingsWithPremiums > 0
  ) {
    return product.projectedSavingsWithPremiums / product.schumKitzbatZikna;
  }

  const key = `${gender}_${isMarried ? "married" : "single"}`;
  const table = DEFAULT_MEKADEM_TABLE[key] ?? DEFAULT_MEKADEM_TABLE.male_single;
  return Math.round(interpolateMekadem(table, retirementAge));
}

/**
 * Calculate monthly pension (kitzba) from accumulated savings and mekadem.
 */
export function calculateMonthlyPension(totalAccumulated: number, mekadem: number): number {
  if (mekadem <= 0) return 0;
  return totalAccumulated / mekadem;
}

// ============================================================================
// EARLY KITZBA (ANNUITY STARTED BEFORE OFFICIAL PENSION AGE)
// ============================================================================

/** Life event tag used to persist per-product early pension withdrawal choices. */
export const PENSION_EARLY_SELL_TAG = "pension_early_sell";

/** Minimum age at which an early pension kitzba can start (Israeli law). */
export const MIN_EARLY_KITZBA_AGE = 60;

/**
 * Mekadem penalty added for each year the kitzba starts before the official
 * pension age. A higher mekadem means a lower monthly payment.
 */
export const EARLY_KITZBA_MEKADEM_PENALTY_PER_YEAR = 12;

/**
 * Whether a withdrawal type has a kitzba (annuity) component that can be
 * started early. HON products pay out only as a lump sum, so they are excluded.
 */
export function isKitzbaCapable(withdrawalType: PensionWithdrawalType): boolean {
  return (
    withdrawalType === PensionWithdrawalType.KITZBA ||
    withdrawalType === PensionWithdrawalType.MIXED
  );
}

/**
 * Compute the mekadem for a kitzba that starts before the official pension age.
 * The coefficient rises by EARLY_KITZBA_MEKADEM_PENALTY_PER_YEAR for each year
 * the kitzba starts before the official age, so the monthly payment is lower
 * (you get money earlier but less of it each month). The start age is floored
 * at MIN_EARLY_KITZBA_AGE.
 */
export function getEarlyKitzbaMekadem(
  baseMekadem: number,
  officialAge: number,
  startAge: number,
): number {
  const clampedStartAge = Math.max(startAge, MIN_EARLY_KITZBA_AGE);
  const yearsEarly = Math.max(0, officialAge - clampedStartAge);
  return baseMekadem + EARLY_KITZBA_MEKADEM_PENALTY_PER_YEAR * yearsEarly;
}

// ============================================================================
// TAX CALCULATIONS
// ============================================================================

export const PENSION_TAX = {
  EARLY_WITHDRAWAL_RATE: 0.35,
  LUMP_SUM_CAPITAL_GAINS_RATE: 0.15,

  // Kitzba Mezaka (qualifying pension) - tax-exempt portion
  KITZBA_MEZAKA_THRESHOLD_MONTHLY: 9_430,
  KITZBA_MEZAKA_EXEMPTION: {
    2025: 0.57,
    2026: 0.60,
    2027: 0.635,
    2028: 0.67,
  } as Record<number, number>,
  KITZBA_MEZAKA_EXEMPTION_DEFAULT: 0.67,
} as const;

/**
 * Get the kitzba mezaka exemption rate for a given year.
 * Returns the rate in effect (gradually increasing to 67%).
 */
export function getKitzbaMezakaExemption(year: number): number {
  if (year <= 2025) return PENSION_TAX.KITZBA_MEZAKA_EXEMPTION[2025];
  if (year >= 2028) return PENSION_TAX.KITZBA_MEZAKA_EXEMPTION_DEFAULT;
  return PENSION_TAX.KITZBA_MEZAKA_EXEMPTION[year] ?? PENSION_TAX.KITZBA_MEZAKA_EXEMPTION_DEFAULT;
}

/**
 * Calculate tax on a monthly kitzba (pension annuity).
 *
 * The qualifying pension threshold (kitzba mezaka) determines the tax-exempt
 * portion. Up to the exemption amount is tax-free; the remainder is taxed
 * at the marginal income tax rate (approximated here).
 */
export function calculateKitzbaTax(
  monthlyPension: number,
  year: number,
  marginalTaxRate: number = 0.30,
): { taxAmount: number; netPension: number; exemptAmount: number } {
  const exemptionRate = getKitzbaMezakaExemption(year);
  const exemptAmount = Math.min(
    monthlyPension,
    PENSION_TAX.KITZBA_MEZAKA_THRESHOLD_MONTHLY * exemptionRate,
  );
  const taxableAmount = Math.max(0, monthlyPension - exemptAmount);
  const taxAmount = taxableAmount * marginalTaxRate;

  return {
    taxAmount,
    netPension: monthlyPension - taxAmount,
    exemptAmount,
  };
}

/**
 * Calculate tax on early withdrawal (before retirement age).
 * Flat 35% tax on the entire withdrawal amount.
 */
export function calculateEarlyWithdrawalTax(amount: number): number {
  return amount * PENSION_TAX.EARLY_WITHDRAWAL_RATE;
}

/**
 * Calculate tax on lump-sum withdrawal (hon) under Amendment 190.
 * 15% capital gains tax on the gain component.
 *
 * The gain is approximated as: totalAmount - totalContributions.
 * If contributions data is unavailable, we assume 50% of the amount is gain.
 */
export function calculateLumpSumTax(
  totalAmount: number,
  totalContributions?: number,
): { taxAmount: number; netAmount: number } {
  const gain = totalContributions !== undefined
    ? Math.max(0, totalAmount - totalContributions)
    : totalAmount * 0.5;

  const taxAmount = gain * PENSION_TAX.LUMP_SUM_CAPITAL_GAINS_RATE;
  return {
    taxAmount,
    netAmount: totalAmount - taxAmount,
  };
}

// ============================================================================
// PER-PRODUCT RETIREMENT PLAN
// ============================================================================

export interface PensionProductPlan {
  productId: number | string;
  productName: string;
  productClass: PensionProductClass;
  currentValue: number;
  projectedValueAtRetirement: number;
  withdrawalType: PensionWithdrawalType;
  mekadem: number;
  monthlyPensionAtRetirement: number;
  lumpSumAtRetirement: number;
  lumpSumTax: number;
  eligibilityAge: number;
  monthlyContribution: number;
  /** Masleka's projected monthly pension (for reference/comparison) */
  maslekaProjectedMonthlyPension?: number;
  /** Masleka's projected savings with premiums */
  maslekaProjectedSavings?: number;
  /** sug_kupa from masleka (1=kitzbati, 2=honi, 3=hishtalmut) */
  sugKupa?: string;
  /** Whether user opted for early withdrawal */
  earlyWithdrawal?: boolean;
  /** Tax amount if withdrawn early (35% flat) */
  earlyWithdrawalTax?: number;
  /** Net amount after early withdrawal tax */
  earlyWithdrawalNet?: number;
  /** Liquidity date for gemel/hishtalmut products */
  liquidityDate?: string;
  /** Product type string from masleka */
  productTypeLabel?: string;
  /** Age at which an early kitzba starts (only set when early kitzba applies) */
  earlyKitzbaStartAge?: number;
  /** Penalized mekadem used for the early kitzba */
  earlyKitzbaMekadem?: number;
  /** Monthly gross kitzba if the kitzba is started early */
  earlyKitzbaMonthlyPension?: number;
  /** Projected accumulated (kitzba-portion) value at the early kitzba start age */
  earlyKitzbaProjectedValue?: number;
  /** Owner id (from pension product id_number) for couple attribution */
  ownerId?: string;
  /** Owner display name (from pension product account_owner) */
  ownerName?: string;
  /** Whether this product belongs to the partner (vs. the primary partner) */
  belongsToPartner?: boolean;
}

/**
 * Build a retirement plan for a single pension product, determining how it
 * will pay out (kitzba vs hon) and calculating projected values.
 */
export function buildProductPlan(
  product: PensionAsset & { id?: number | string },
  projectedValue: number,
  retirementAge: number,
  gender: "male" | "female" = "male",
  isMarried: boolean = false,
  earlyKitzba?: { startAge: number; projectedValueAtStart: number; officialAge: number },
  ownerInfo?: { ownerId?: string; ownerName?: string; belongsToPartner?: boolean },
): PensionProductPlan {
  const productClass = classifyPensionProduct(product);
  const withdrawalType = getDefaultWithdrawalType(productClass, product.sugKupa);
  const eligibilityAge = getEligibilityAge(productClass, gender);
  const mekadem = getMekadem(product, retirementAge, gender, isMarried);
  const monthlyContribution = (product.monthlyDepositEmployee || 0) + (product.monthlyDepositEmployer || 0);

  let monthlyPensionAtRetirement = 0;
  let lumpSumAtRetirement = 0;
  let lumpSumTax = 0;

  if (withdrawalType === PensionWithdrawalType.KITZBA) {
    monthlyPensionAtRetirement = calculateMonthlyPension(projectedValue, mekadem);
  } else if (withdrawalType === PensionWithdrawalType.HON) {
    const taxResult = calculateLumpSumTax(projectedValue);
    lumpSumAtRetirement = taxResult.netAmount;
    lumpSumTax = taxResult.taxAmount;
  } else {
    // Mixed (Bituach Menahalim without sug_kupa): 70% kitzba, 30% hon
    const kitzbaPortion = projectedValue * 0.7;
    const honPortion = projectedValue * 0.3;
    monthlyPensionAtRetirement = calculateMonthlyPension(kitzbaPortion, mekadem);
    const taxResult = calculateLumpSumTax(honPortion);
    lumpSumAtRetirement = taxResult.netAmount;
    lumpSumTax = taxResult.taxAmount;
  }

  const currentValue = product.amount || 0;
  const earlyTax = calculateEarlyWithdrawalTax(currentValue);

  // Early kitzba: when the user retires early and elects to draw a kitzba before
  // the official pension age, the mekadem is penalized (+12 per year early) and
  // only the kitzba portion of the product applies. HON products are excluded.
  let earlyKitzbaStartAge: number | undefined;
  let earlyKitzbaMekadem: number | undefined;
  let earlyKitzbaMonthlyPension: number | undefined;
  let earlyKitzbaProjectedValue: number | undefined;
  if (earlyKitzba && isKitzbaCapable(withdrawalType)) {
    const baseMekadem = getMekadem(product, earlyKitzba.officialAge, gender, isMarried);
    const penalizedMekadem = getEarlyKitzbaMekadem(
      baseMekadem,
      earlyKitzba.officialAge,
      earlyKitzba.startAge,
    );
    // MIXED products only start the 70% kitzba portion early; the 30% hon stays.
    const kitzbaShare = withdrawalType === PensionWithdrawalType.MIXED ? 0.7 : 1;
    const kitzbaProjected = earlyKitzba.projectedValueAtStart * kitzbaShare;
    earlyKitzbaStartAge = Math.max(earlyKitzba.startAge, MIN_EARLY_KITZBA_AGE);
    earlyKitzbaMekadem = penalizedMekadem;
    earlyKitzbaProjectedValue = kitzbaProjected;
    earlyKitzbaMonthlyPension = calculateMonthlyPension(kitzbaProjected, penalizedMekadem);
  }

  return {
    productId: product.id ?? product.name,
    productName: product.name,
    productClass,
    currentValue,
    projectedValueAtRetirement: projectedValue,
    withdrawalType,
    mekadem,
    monthlyPensionAtRetirement,
    lumpSumAtRetirement,
    lumpSumTax,
    eligibilityAge,
    monthlyContribution,
    maslekaProjectedMonthlyPension: product.projectedMonthlyPension ?? product.schumKitzbatZikna,
    maslekaProjectedSavings: product.projectedSavingsWithPremiums,
    sugKupa: product.sugKupa,
    earlyWithdrawal: false,
    earlyWithdrawalTax: earlyTax,
    earlyWithdrawalNet: currentValue - earlyTax,
    productTypeLabel: product.productType?.value,
    earlyKitzbaStartAge,
    earlyKitzbaMekadem,
    earlyKitzbaMonthlyPension,
    earlyKitzbaProjectedValue,
    ownerId: ownerInfo?.ownerId,
    ownerName: ownerInfo?.ownerName,
    belongsToPartner: ownerInfo?.belongsToPartner,
  };
}

// ============================================================================
// PENSION SCENARIO PROJECTION
// ============================================================================

export interface PensionScenarioPoint {
  age: number;
  year: number;
  totalValue: number;
  monthlyPensionIfStartNow: number;
  monthlyPensionNetIfStartNow: number;
  lumpSumIfWithdrawnNow: number;
  earlyWithdrawalNet: number;
  isRetired: boolean;
  /** Monthly pension income for this year (only if pension is active) */
  pensionIncome: number;
}

export interface PensionScenarioResult {
  /** One point per year from current age to life expectancy */
  points: PensionScenarioPoint[];
  /** Scenarios comparing different pension start ages */
  scenarios: {
    startAge: number;
    monthlyPension: number;
    monthlyPensionNet: number;
    totalValueAtStart: number;
    /** Cumulative pension income received by life expectancy */
    cumulativeIncome: number;
  }[];
}

/**
 * Project pension value over time and calculate scenarios for different start ages.
 * This is a simplified projection focused on pension only (not the full portfolio).
 */
export function projectPensionScenarios(
  plans: PensionProductPlan[],
  currentAge: number,
  lifeExpectancy: number,
  pensionReturnRate: number,
  inflation: number,
  gender: "male" | "female" = "male",
  isMarried: boolean = false,
): PensionScenarioResult {
  const currentYear = new Date().getFullYear();
  const totalCurrentValue = plans.reduce((s, p) => s + p.currentValue, 0);
  const totalMonthlyContrib = plans.reduce((s, p) => s + p.monthlyContribution, 0);

  const points: PensionScenarioPoint[] = [];

  for (let age = currentAge; age <= lifeExpectancy; age++) {
    const yearsFromNow = age - currentAge;
    const year = currentYear + yearsFromNow;
    const growthFactor = Math.pow(1 + pensionReturnRate, yearsFromNow);
    const totalContribs = totalMonthlyContrib * 12 * yearsFromNow;

    // Simplified FV: current value grows + contributions grow at half rate
    const projectedValue = totalCurrentValue * growthFactor
      + totalContribs * Math.pow(1 + pensionReturnRate / 2, yearsFromNow);

    // What would monthly pension be if started at this age?
    const key = `${gender}_${isMarried ? "married" : "single"}`;
    const table = DEFAULT_MEKADEM_TABLE[key] ?? DEFAULT_MEKADEM_TABLE.male_single;
    const ageSpecificMekadem = interpolateMekadem(table, age);

    // Use product-specific mekadem if available, weighted by value
    let weightedMekadem = 0;
    let totalWeight = 0;
    for (const plan of plans) {
      if (plan.withdrawalType === PensionWithdrawalType.HON) continue;
      const w = plan.currentValue || 1;
      const m = plan.mekadem > 0 ? plan.mekadem : ageSpecificMekadem;
      weightedMekadem += m * w;
      totalWeight += w;
    }
    const effectiveMekadem = totalWeight > 0 ? weightedMekadem / totalWeight : ageSpecificMekadem;

    // Separate kitzba and hon portions
    let kitzbaPortion = 0;
    let honPortion = 0;
    for (const plan of plans) {
      const planShare = totalCurrentValue > 0 ? plan.currentValue / totalCurrentValue : 1 / plans.length;
      const planProjected = projectedValue * planShare;
      if (plan.withdrawalType === PensionWithdrawalType.KITZBA) {
        kitzbaPortion += planProjected;
      } else if (plan.withdrawalType === PensionWithdrawalType.HON) {
        honPortion += planProjected;
      } else {
        kitzbaPortion += planProjected * 0.7;
        honPortion += planProjected * 0.3;
      }
    }

    const monthlyPension = effectiveMekadem > 0 ? kitzbaPortion / effectiveMekadem : 0;
    const taxResult = calculateKitzbaTax(monthlyPension, year);
    const earlyWithdrawalNet = projectedValue * (1 - PENSION_TAX.EARLY_WITHDRAWAL_RATE);
    const lumpSumNet = honPortion * (1 - PENSION_TAX.LUMP_SUM_CAPITAL_GAINS_RATE);

    points.push({
      age,
      year,
      totalValue: Math.round(projectedValue),
      monthlyPensionIfStartNow: Math.round(monthlyPension),
      monthlyPensionNetIfStartNow: Math.round(taxResult.netPension),
      lumpSumIfWithdrawnNow: Math.round(lumpSumNet),
      earlyWithdrawalNet: Math.round(earlyWithdrawalNet),
      isRetired: false,
      pensionIncome: 0,
    });
  }

  // Build comparison scenarios for different start ages
  const scenarioAges = [60, 62, 65, 67, 70].filter(a => a >= currentAge && a <= lifeExpectancy);
  const scenarios = scenarioAges.map(startAge => {
    const point = points.find(p => p.age === startAge);
    if (!point) return null;

    const monthlyPension = point.monthlyPensionIfStartNow;
    const monthlyPensionNet = point.monthlyPensionNetIfStartNow;
    const yearsReceiving = lifeExpectancy - startAge;
    const cumulativeIncome = monthlyPensionNet * 12 * yearsReceiving;

    return {
      startAge,
      monthlyPension,
      monthlyPensionNet,
      totalValueAtStart: point.totalValue,
      cumulativeIncome: Math.round(cumulativeIncome),
    };
  }).filter((s): s is NonNullable<typeof s> => s !== null);

  return { points, scenarios };
}

// ============================================================================
// CONTRIBUTION REDUCTION
// ============================================================================

/**
 * Calculate the reduced pension contribution after an income reduction event.
 *
 * When a person's income drops (e.g. they go part-time or retire), their
 * pension contributions drop proportionally because contributions are
 * percentage-based on salary.
 *
 * @param currentMonthlyContribution Current total monthly contribution
 * @param incomeReductionRatio How much income was reduced (0-1, e.g. 0.5 = halved)
 * @returns New monthly contribution after reduction
 */
export function calculateReducedContribution(
  currentMonthlyContribution: number,
  incomeReductionRatio: number,
): number {
  return currentMonthlyContribution * Math.max(0, 1 - incomeReductionRatio);
}

// ============================================================================
// MARITAL STATUS HELPERS
// ============================================================================

const MARRIED_STATUSES = new Set(["Married", "2", "Known in public", "6"]);

export function isMarriedStatus(matsavMishpachti?: string): boolean {
  if (!matsavMishpachti) return false;
  return MARRIED_STATUSES.has(matsavMishpachti);
}

export function parseGender(min?: string): "male" | "female" {
  if (!min) return "male";
  const lower = min.toLowerCase();
  if (lower === "female" || lower === "נקבה" || lower === "f") return "female";
  return "male";
}

// ============================================================================
// PRODUCT CLASS DISPLAY NAMES
// ============================================================================

export const PRODUCT_CLASS_NAMES_HEB: Record<PensionProductClass, string> = {
  [PensionProductClass.KEREN_PENSIA_KITZBATIT]: "קרן פנסיה (קצבתי)",
  [PensionProductClass.KEREN_PENSIA_HONIT]: "קרן פנסיה (הוני)",
  [PensionProductClass.BITUACH_MENAHALIM]: "ביטוח מנהלים",
  [PensionProductClass.KUPAT_GEMEL_KITZBATIT]: "קופת גמל (קצבתי)",
  [PensionProductClass.KUPAT_GEMEL_HONIT]: "קופת גמל (הוני)",
  [PensionProductClass.KUPAT_GEMEL_LEHASHKAA]: "קופת גמל להשקעה",
  [PensionProductClass.KEREN_HISHTALMUT]: "קרן השתלמות",
  [PensionProductClass.CHISACHON_LEKAL_YELED]: "חיסכון לכל ילד",
  [PensionProductClass.UNKNOWN]: "לא ידוע",
};

export const PRODUCT_CLASS_NAMES_ENG: Record<PensionProductClass, string> = {
  [PensionProductClass.KEREN_PENSIA_KITZBATIT]: "Pension Fund (Annuity)",
  [PensionProductClass.KEREN_PENSIA_HONIT]: "Pension Fund (Lump Sum)",
  [PensionProductClass.BITUACH_MENAHALIM]: "Manager Insurance",
  [PensionProductClass.KUPAT_GEMEL_KITZBATIT]: "Provident Fund (Annuity)",
  [PensionProductClass.KUPAT_GEMEL_HONIT]: "Provident Fund (Lump Sum)",
  [PensionProductClass.KUPAT_GEMEL_LEHASHKAA]: "Investment Provident Fund",
  [PensionProductClass.KEREN_HISHTALMUT]: "Education Fund",
  [PensionProductClass.CHISACHON_LEKAL_YELED]: "Child Savings",
  [PensionProductClass.UNKNOWN]: "Unknown",
};
